import { motion } from "framer-motion";
import { Check, Armchair, ArrowUpRight } from "lucide-react";
import { SERVICES, CUMMINS_MODELS, ASIENTOS, CONTACT } from "../../data/content";

const ServiceCard = ({ s, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: i * 0.1 }}
    className="group bg-[#141414] border border-[#262626] hover:border-[#FF1616] transition-colors overflow-hidden flex flex-col"
    data-testid={`service-card-${s.id}`}
  >
    <div className="relative h-52 overflow-hidden bg-white">
      <img src={s.image} alt={s.brand} className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500" />
      <span className="absolute top-4 left-4 bg-[#FF1616] text-black text-xs font-heading font-bold uppercase tracking-wider px-3 py-1">
        {s.tag}
      </span>
      {s.logo && (
        <img
          src={s.logo}
          alt={`Logo ${s.brand}`}
          className="absolute top-4 right-4 h-10 w-auto max-w-[80px] object-contain"
        />
      )}
    </div>
    <div className="p-7 flex flex-col flex-1">
      <h3 className="font-heading font-black uppercase text-white text-xl tracking-tight">{s.brand}</h3>
      <p className="font-body text-sm text-neutral-400 mt-2 leading-relaxed">{s.desc}</p>
      <ul className="mt-5 space-y-2 flex-1">
        {s.points.map((p) => (
          <li key={p} className="flex items-center gap-2 font-body text-sm text-neutral-300">
            <Check size={16} className="text-[#FF1616] shrink-0" /> {p}
          </li>
        ))}
      </ul>
    </div>
  </motion.div>
);

export const Services = () => {
  const waAsientos = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hola JAN SMM, me interesan los asientos para equipos. ¿Me comparten disponibilidad?")}`;
  return (
    <section id="servicios" data-testid="services-section" className="bg-[#0F0F0F] py-24 border-t border-[#262626]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <span className="font-body text-sm tracking-[0.25em] uppercase text-[#FF1616]">Líneas de Servicio</span>
          <h2 className="font-heading font-black uppercase text-white text-3xl sm:text-4xl tracking-tight mt-3">
            Especialistas en motores diésel para minería
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} s={s} i={i} />
          ))}
        </div>

        {/* Cummins models table */}
        <div className="grid lg:grid-cols-2 gap-8 mt-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            data-testid="cummins-models-table"
            className="bg-[#141414] border border-[#262626]"
          >
            <div className="px-6 py-4 border-b border-[#262626] flex items-center justify-between">
              <h3 className="font-heading font-bold uppercase text-white tracking-wide">Modelos Cummins disponibles</h3>
              <span className="text-xs font-body uppercase tracking-wider text-[#FF1616]">Caballaje</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3">
              {CUMMINS_MODELS.map((m, idx) => (
                <div key={idx} className="px-5 py-4 border-b border-r border-[#262626] hover:bg-[#1E1E1E] transition-colors">
                  <div className="font-heading font-bold text-white">{m.modelo}</div>
                  <div className="font-body text-sm text-[#FF1616]">{m.hp}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Asientos */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            data-testid="service-card-asientos"
            className="border border-[#262626] bg-[#141414] overflow-hidden h-full flex flex-col"
          >
            {/* Fotos oficiales de catálogo Grammer — cada celda con su fondo blanco completo */}
            <div className="grid grid-cols-3 gap-px bg-neutral-200">
              {ASIENTOS.fotos.map((f) => (
                <div key={f.src} className="bg-white h-52 flex items-center justify-center p-2">
                  <img src={f.src} alt={f.alt} className="h-full w-full object-contain" />
                </div>
              ))}
            </div>

            <div className="p-7 flex flex-col flex-1">
              <div className="flex items-center gap-2 text-[#FF1616]">
                <Armchair size={26} />
                <h3 className="font-heading font-black uppercase text-white text-xl tracking-tight">
                  Asientos para equipos
                </h3>
              </div>
              <p className="font-body text-sm text-neutral-400 mt-2">
                Venta de asientos para equipos de minería, construcción y camiones.
              </p>

              <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                {ASIENTOS.aplicaciones.map((a) => (
                  <li key={a} className="flex items-center gap-2 font-body text-sm text-neutral-300">
                    <Check size={16} className="text-[#FF1616] shrink-0" /> {a}
                  </li>
                ))}
              </ul>

              {/* Logos de las marcas de asientos — en blanco, sin fondo */}
              <div className="flex items-center gap-6 mt-6">
                <span className="font-body text-xs uppercase tracking-wider text-neutral-500">Marcas:</span>
                {ASIENTOS.logos.map((l) => (
                  <img
                    key={l.src}
                    src={l.src}
                    alt={`Logo ${l.alt}`}
                    className="h-9 w-auto object-contain opacity-90"
                  />
                ))}
              </div>

              <a
                href={waAsientos}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="asientos-cta"
                className="inline-flex items-center gap-1 mt-auto pt-6 font-heading font-bold uppercase text-sm text-[#FF1616] hover:gap-2 transition-all"
              >
                Consultar disponibilidad <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
